import Icon, { IconType } from 'components/Icon';
import ActionButton from 'components/Widget/Button/ActionButton';
import ChatAttachmentTile, { ChatAttachmentTileState } from 'components/Widget/ChatAttachmentTile';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import { useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { showContextMenu } from 'redux/mode/actions';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { UploadTracker } from 'utils';
import { toast } from 'react-toastify';
import { FileUploadCallbacks, onUploadFileDialog, onFilePasted } from 'utils';
import { interceptSubmissionIfWalletEmpty } from 'economy';
import QualityToggle from 'components/Conversation/ChatInput/QualityToggle';
import * as S from './styles';
import { PROMPT_EXAMPLES } from './constants';

interface ProjectCreatorInputProps {
	onSubmit: (prompt: string, attachments: Attachment[]) => Promise<void>;
	disabled?: boolean;
}

const ProjectCreatorInput = ({ onSubmit, disabled = false }: ProjectCreatorInputProps) => {
	const dispatch = useDispatch();
	const { isMobile } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);

	const [prompt, setPrompt] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [loadingAttachments, setLoadingAttachments] = useState<UploadTracker[]>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [undoStack, setUndoStack] = useState<string[]>([]);
	const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	// Validation logic for whether input can be submitted
	const hasLoadingAttachments = useMemo(
		() => loadingAttachments && loadingAttachments.length > 0,
		[loadingAttachments]
	);
	const minimalInputEntered = useMemo(
		() => prompt.length > 0 || attachments.length > 0,
		[prompt, attachments]
	);
	const canSubmit = minimalInputEntered && !hasLoadingAttachments && !disabled && !isSubmitting;

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const handleSubmit = async () => {
		if (!canSubmit) return;

		if (interceptSubmissionIfWalletEmpty()) {
			return;
		}

		setIsSubmitting(true);
		try {
			await onSubmit(prompt, attachments);
			// Clear state after successful submission
			setPrompt('');
			setAttachments([]);
		} catch (error) {
			// Error handling is done in parent component
		} finally {
			setIsSubmitting(false);
		}
	};

	const onChangePrompt = (e: any) => {
		setPrompt(e.target.value);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		// Handle Ctrl+Z/Cmd+Z for undo
		if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
			if (undoStack.length > 0) {
				e.preventDefault();
				const previousPrompt = undoStack[undoStack.length - 1];
				setUndoStack(undoStack.slice(0, -1));
				setPrompt(previousPrompt);
				// Focus input after undo
				setTimeout(() => {
					inputRef.current?.focus();
				}, 0);
			}
			return;
		}

		// On desktop: Enter submits, Shift+Enter adds line break
		// On mobile: Enter adds line break, user taps button to submit
		if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
			e.preventDefault();
			if (canSubmit) {
				handleSubmit();
			}
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		setAttachments(attachments.filter((a) => a.storagePath !== storagePath));
	};

	const handlePaste = (text: string) => {
		// Insert the pasted text at the current cursor position in the prompt
		const textarea = inputRef.current;
		if (textarea) {
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const newPrompt = prompt.substring(0, start) + text + prompt.substring(end);
			setPrompt(newPrompt);

			// Focus the input after pasting
			setTimeout(() => {
				textarea.focus();
				textarea.setSelectionRange(start + text.length, start + text.length);
			}, 0);
		} else {
			// Fallback if textarea ref is not available
			setPrompt(prompt + text);
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	};

	const onContextMenu = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (e.target instanceof HTMLElement) {
			const paste = await MenuItemAction.createPasteText(handlePaste);
			const menuItems = [paste];
			const contextMenuAction = showContextMenu({
				point: { x: e.clientX + 5, y: e.clientY },
				menuItems,
			});
			dispatch(contextMenuAction);
		}
	};

	// File upload callbacks with functional setState to avoid stale closures
	const fileCallbacks: FileUploadCallbacks = useMemo(
		() => ({
			onLoadingStart: (uploadTracker: UploadTracker) => {
				setLoadingAttachments((prev) => [...prev, uploadTracker]);
			},
			onLoadingEnd: (uniqueId: string) => {
				setLoadingAttachments((prev) => prev.filter((a) => a.uniqueId !== uniqueId));
			},
			onSuccess: (attachment: Attachment) => {
				setAttachments((prev) => {
					// Check if file already exists
					if (prev.some((a) => a.storagePath === attachment.storagePath)) {
						toast.warning('This file is already attached');
						return prev;
					}
					return [...prev, attachment];
				});
			},
			onError: (error: Error) => {
				console.error('Error uploading file:', error);
			},
		}),
		[]
	);

	const handleTryExample = () => {
		// Save current prompt to undo stack if it exists
		if (prompt.trim().length > 0) {
			setUndoStack([...undoStack, prompt]);
		}

		// Select a random prompt, avoiding the last one used
		let randomIndex;
		do {
			randomIndex = Math.floor(Math.random() * PROMPT_EXAMPLES.length);
		} while (randomIndex === lastSelectedIndex && PROMPT_EXAMPLES.length > 1);

		setLastSelectedIndex(randomIndex);
		setPrompt(PROMPT_EXAMPLES[randomIndex]);

		// Focus input after setting example
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderAttachments = () => {
		const allAttachments = [
			...attachments,
			...loadingAttachments.map((attachment) => ({
				...attachment,
				storagePath: `loading-${attachment.uniqueId}`, // Use uniqueId for loading attachments
			})),
		];

		if (!allAttachments.length) return null;
		return (
			<S.Attachments
				onWheel={(e) => {
					e.stopPropagation();
					e.currentTarget.scrollLeft += e.deltaX;
				}}
			>
				{allAttachments.map((attachment) => {
					const isLoading = loadingAttachments.some(
						(a) => a.uniqueId === attachment.storagePath.replace('loading-', '')
					);
					return (
						<ChatAttachmentTile
							key={attachment.storagePath}
							attachment={attachment}
							onDelete={isLoading ? undefined : () => onRemoveAttachment(attachment.storagePath)}
							state={isLoading ? ChatAttachmentTileState.LOADING : ChatAttachmentTileState.REGULAR}
						/>
					);
				})}
			</S.Attachments>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<ActionButton
				icon={IconType.ADD}
				tooltip="Upload attachment"
				onClick={() => onUploadFileDialog(fileCallbacks)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<S.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				<QualityToggle />
				<S.Spacer />
				{isSubmitting ? (
					<S.LoadingWrapper isMobile={isMobile}>
						<Loader width={isMobile ? 32 : 22} height={isMobile ? 32 : 22} />
					</S.LoadingWrapper>
				) : (
					<S.SubmitButtonWrapper
						isMobile={isMobile}
						isDisabled={!canSubmit}
						onClick={canSubmit ? handleSubmit : undefined}
						disabled={!canSubmit}
					>
						<S.SubmitButtonIcon>
							<Icon type={IconType.LINE_ARROW_UP} width={16} height={16} color="#FFFFFF" />
						</S.SubmitButtonIcon>
						<S.SubmitButtonText>Create Design</S.SubmitButtonText>
					</S.SubmitButtonWrapper>
				)}
			</S.Actions>
		);
	};

	return (
		<S.Base isMobile={isMobile}>
			<S.Prompt isFocused={isFocused}>
				{renderAttachments()}
				<S.MultilineInputWrapper isMobile={isMobile} onPaste={onFilePasted(fileCallbacks)}>
					<MultilineInput
						ref={inputRef}
						value={prompt}
						onInput={onChangePrompt}
						onKeyDown={onKeyDown}
						placeholder={'Describe your design idea...'}
						minLines={2}
						renderBorder={false}
						variant="large"
						onFocusChange={(isFocused) => {
							setIsFocused(isFocused);
						}}
						onContextMenu={onContextMenu}
					/>
				</S.MultilineInputWrapper>
				{renderActions()}
			</S.Prompt>
			<S.ExamplePromptLink onClick={handleTryExample}>Try an example</S.ExamplePromptLink>
		</S.Base>
	);
};

export default ProjectCreatorInput;
